/* eslint-disable no-unused-expressions */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { DEFAULT_ROTATION_ANGLE } from 'constants/photoSession';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SortableElement } from 'react-sortable-hoc';
import { Delete } from '@quintoandar/cozy-icons/Delete';
import { StarOn } from '@quintoandar/cozy-icons/StarOn';
import { StarOff } from '@quintoandar/cozy-icons/StarOff';
import { Rotate } from '@quintoandar/cozy-icons/Rotate';
import Box from '@quintoandar/cozy-core/Box';
import Form from '@quintoandar/cozy-core/Form';
import Input from '@quintoandar/cozy-core/Input/Input';
import Autocomplete from '@quintoandar/cozy-core/Autocomplete';
import IconButton from '@quintoandar/cozy-core/IconButton';
import tokens from '@quintoandar/cozy-theme/tokens/js';
import {
  deletePhotosAction,
  setUpdateDescriptionAction,
  updateMainPhotoAction,
  rotatePhotoClockwiseAction,
} from 'containers/PhotosManager/action';
import PhotosphereViewer from './PhotosphereViewer';
import messages from './messages';
import { roomsSuggestions } from './roomSuggestions';

import {
  Card,
  CardImage,
  RotateIconWrapper,
  DeleteIconWrapper,
  SelectMainIconWrapper,
  PhotosManagerModal,
  Loading,
  CardContentWrapper,
  CardImageModal,
} from './elements';

// Without using "React.forwardRef", the PhotosphereViewer component won't render
// properly, due to Pannellun (360 photos render) losing its refs when turned into a component
export const PhotosphereViewerWithRef = React.forwardRef((props, ref) => (
  <PhotosphereViewer {...props} ref={ref} />
));

const buildOptions = (list, description) => {
  const sortedList = list.slice().sort((a, b) => a.localeCompare(b));
  const shouldIncludeDescription = description && !sortedList.includes(description);
  const newList = shouldIncludeDescription ? [...sortedList, description] : sortedList;
  return newList.map(label => ({ label }));
};

const iconButtonProps = {
  variant: 'fixed',
  showTooltip: true,
};

export const PhotosGalleryCardItem = ({
  houseId,
  galleryType,
  path,
  previewPath,
  description: initialDescription,
  id,
  deletePhoto,
  setUpdateDescription,
  selectMainPhoto,
  intl,
  isPrincipal,
  rotatePhoto,
  direction,
  isDragging,
}) => {
  const imgRef = useRef(null);

  const [description, setDescription] = useState(initialDescription || '');
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [degrees, setDegrees] = useState(0);
  const selectedItem = useRef({ label: description });

  const handleOpen = useCallback(() => setIsOpen(true));
  const handleClose = useCallback(() => setIsOpen(false));
  const handleDelete = useCallback(
    () => {
      setIsBeingDeleted(true);
      deletePhoto(id, galleryType, houseId);
    },
    [id, galleryType, houseId],
  );

  const handleDescriptionChange = useCallback(
    (value = '') => {
      setDescription(value);
      setUpdateDescription(id, value);
    },
    [id],
  );

  const handlePhotosphereDescriptionChange = useCallback(event => {
    handleDescriptionChange(event.target.value);
  }, []);

  const handleSelectMainPhoto = useCallback(
    () => {
      selectMainPhoto(
        id,
        houseId,
        imgRef.current?.naturalWidth,
        imgRef.current?.naturalHeight,
      );
    },
    [id, houseId],
  );

  const handleRotatePhoto = useCallback(
    () => {
      setDegrees(degrees + DEFAULT_ROTATION_ANGLE);
      rotatePhoto(houseId, id, direction);
    },
    [degrees, houseId, id, direction],
  );

  const displayRotateButton = () => (
    <RotateIconWrapper mr={1} mb={1}>
      <IconButton
        {...iconButtonProps}
        data-testid="rotate-icon"
        icon={Rotate}
        onClick={handleRotatePhoto}
      >
        <FormattedMessage {...messages.RotateIconTooltip} />
      </IconButton>
    </RotateIconWrapper>
  );

  const mainIconRender = principal => (
    <SelectMainIconWrapper ml={1} mt={1} display={principal ? 'block !important' : undefined}>
      <IconButton
        {...iconButtonProps}
        data-testid="main-icon"
        icon={principal ? StarOn : StarOff}
        onClick={handleSelectMainPhoto}
      >
        <FormattedMessage {...messages.selectMainIconTooltip} />
      </IconButton>
    </SelectMainIconWrapper>
  );

  const handleRoomsSuggestionsChange = useCallback(data => {
    handleDescriptionChange(data.label);
  }, []);

  const handleAutocompleteInputChange = useCallback(e => {
    handleDescriptionChange(e.target.value);
  }, []);

  const options = buildOptions(roomsSuggestions(intl), description);

  const isGalleryTypePhoto = galleryType === 'photo';

  useEffect(() => {
    const currentItem = options.find(item => item.label === description);
    selectedItem.current.label = currentItem?.label || '';
  }, [options, description]);

  return (
    <Card
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      bgcolor={tokens.alias.color.surface.selected.highlight}
      data-testid="card-component"
    >
      {isBeingDeleted && (
        <Loading size={50} data-testid="loading-conditional-component" />
      )}

      <CardContentWrapper transparency={isBeingDeleted}>
        <Box position="relative">
          <Box
            overflow="hidden"
            borderRadius="8px 8px 0 0"
            onClick={isDragging ? undefined : handleOpen}
          >
            <CardImage
              ref={imgRef}
              degrees={degrees}
              src={previewPath}
              loading="lazy"
            />
          </Box>

          {isGalleryTypePhoto ? displayRotateButton() : null}

          <DeleteIconWrapper mr={1} mt={1}>
            <IconButton
              {...iconButtonProps}
              data-testid="delete-icon"
              icon={Delete}
              onClick={handleDelete}
            >
              <FormattedMessage {...messages.deleteIconTooltip} />
            </IconButton>
          </DeleteIconWrapper>

          {isGalleryTypePhoto ? mainIconRender(isPrincipal) : null}
        </Box>
        <Box p={1}>
          {isGalleryTypePhoto ? (
            <Autocomplete
              label={messages.autocomplete}
              onChange={handleRoomsSuggestionsChange}
              selectedItem={selectedItem.current}
              dataTestId="autocomplete"
              suggestions={options}
              placeholder={intl.formatMessage(messages.description)}
              inputProps={{
                onChange: handleAutocompleteInputChange,
              }}
            />
          ) : (
            <Form>
              <Input
                name="placeholder"
                fullWidth
                value={description}
                onChange={handlePhotosphereDescriptionChange}
                placeholder={intl.formatMessage(messages.description)}
                inputProps={{
                  'data-testid': 'text-input',
                  disableUnderline: true,
                }}
                variant="outlined"
                autoComplete="off"
              />
            </Form>
          )}
        </Box>

        <PhotosManagerModal open={isOpen} onClose={handleClose}>
          {galleryType === 'photosphere' ? (
            <PhotosphereViewerWithRef id={id.toString()} path={path} />
          ) : (
            <CardImageModal degrees={degrees} src={path} role="presentation" />
          )}
        </PhotosManagerModal>
      </CardContentWrapper>
    </Card>
  );
};

PhotosGalleryCardItem.propTypes = {
  houseId: PropTypes.string,
  galleryType: PropTypes.string,
  path: PropTypes.string,
  previewPath: PropTypes.string,
  description: PropTypes.string,
  id: PropTypes.number,
  deletePhoto: PropTypes.func,
  setUpdateDescription: PropTypes.func,
  intl: PropTypes.object,
  selectMainPhoto: PropTypes.func,
  isPrincipal: PropTypes.string,
  rotatePhoto: PropTypes.func,
  direction: PropTypes.string,
  isDragging: PropTypes.bool,
};

export const mapDispatchToProps = dispatch => ({
  deletePhoto: (photoId, galleryType, houseId) =>
    dispatch(deletePhotosAction(photoId, galleryType, houseId)),
  setUpdateDescription: (id, description) =>
    dispatch(setUpdateDescriptionAction(id, description)),
  selectMainPhoto: (id, houseId, width, height) =>
    dispatch(updateMainPhotoAction(id, houseId, width, height)),
  rotatePhoto: (id, houseId, direction) =>
    dispatch(rotatePhotoClockwiseAction(id, houseId, direction)),
});

const withConnect = connect(
  null,
  mapDispatchToProps,
);

export default compose(
  SortableElement,
  injectIntl,
  withConnect,
)(PhotosGalleryCardItem);
